/**
 * `/invite/$token` — accept an invitation against worker-auth (auth-and-authz.md §4; fixtures:
 * invite-metadata-*, invite-accept-*). The metadata's `account_exists` picks the flow:
 *
 *  - NO account for the invited email → collect a full name + a first password (min 12) and
 *    accept; worker-auth creates the identity and signs it in. Next: TOTP enrolment, then the
 *    firm picker (/firms).
 *  - an EXISTING account → the invite token only authorises the GRANT; worker-auth requires that
 *    account's own signed-in session (at its enrolled factor strength) to accept. So:
 *      · signed in as that account → one-click "Accept invitation";
 *      · not signed in → "Sign in to accept" (via /login, carrying `?redirect=/invite/<token>`,
 *        and back here afterwards);
 *      · signed in as someone else → sign out, then sign in as the invitee.
 *    A server refusal (401 `invite_sign_in_required`) is mapped per `details.reason`:
 *    `session_required` → sign in again; `identity_mismatch` → sign out + sign in as the invitee;
 *    `second_factor_required` → sign in again WITH the authenticator (a password-only session is
 *    not enough for a TOTP-enrolled account). A refusal never consumes the invitation.
 *
 * `firm_name` is optional — without it the screen uses generic wording, never a placeholder.
 * After a successful accept the destination is the firm picker, where the new firm now appears.
 *
 * The new-account form renders the invited address (`meta.email`) as a READONLY
 * `autocomplete="username"` field. A password manager pairs a credential with the username field
 * in the same form; without one this screen — where the password is CREATED — gave managers
 * nothing to attach it to, and the PO accepted a real staging invitation with a password no
 * manager offered to save. It is shown rather than hidden (several managers skip hidden fields)
 * and is never editable: the accept is authorised by the TOKEN, and the rendered address is never
 * read back out of the DOM, so it can never become a way to accept as a different address.
 */
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useId, useState, type FormEvent, type JSX } from 'react';

import {
  AuthCard,
  FormError,
  PasswordField,
  ReadonlyIdentityField,
  SubmitButton,
  TextField,
} from '../components/ui.tsx';
import {
  api,
  getErrorMessage,
  inviteSignInReason,
  type InviteMetadata,
  type InviteSignInReason,
} from '../lib/api.ts';
import { formatRole } from '../lib/format/role.ts';
import { inviteRedirect } from '../router/redirect.ts';
import { resolveAuthReady, useAuth } from '../store/auth-store.ts';

const MIN_PASSWORD = 12;

const linkButton = {
  padding: 0,
  border: 0,
  background: 'transparent',
  color: 'var(--auth-link, #3a5a9a)',
  cursor: 'pointer',
  fontSize: 13,
  textAlign: 'left' as const,
};

/** The copy for each server refusal reason (what happened + what to do). */
function refusalCopy(
  reason: InviteSignInReason,
  invitee: string,
  signedInAs: string | null,
): string {
  switch (reason) {
    case 'session_required':
      return `Your session has ended. Sign in as ${invitee} again to accept this invitation.`;
    case 'identity_mismatch':
      return signedInAs
        ? `You're signed in as ${signedInAs}, but this invitation is for ${invitee}. Sign out, then sign in as ${invitee} to accept it.`
        : `This invitation is for ${invitee}. Sign out, then sign in as ${invitee} to accept it.`;
    case 'second_factor_required':
      return 'For your security, accepting needs a sign-in that used your authenticator app. Sign in again with your authenticator code (or a recovery code) to accept.';
  }
}

const sameEmail = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

export function InviteScreen({ token }: { token: string }): JSX.Element {
  const navigate = useNavigate();
  // Wired as the password field's `aria-describedby` while (and only while) an error is rendered.
  const errorId = useId();
  const refresh = useAuth((s) => s.refresh);
  const signOut = useAuth((s) => s.signOut);
  const status = useAuth((s) => s.status);
  const identity = useAuth((s) => s.identity);

  const [meta, setMeta] = useState<InviteMetadata | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<InviteSignInReason | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const m = await api.invites.get(token);
        // An existing-account invite depends on WHO is signed in: settle the session first.
        if (m.account_exists) await resolveAuthReady();
        if (active) setMeta(m);
      } catch (err) {
        if (active) setLoadError(getErrorMessage(err));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  async function goSignIn(): Promise<void> {
    await navigate({ to: '/login', search: { redirect: inviteRedirect(token) } });
  }

  async function signOutThenSignIn(): Promise<void> {
    await signOut();
    await goSignIn();
  }

  async function handleCreate(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    setBusy(true);
    try {
      const res = await api.invites.accept(token, { password, full_name: fullName.trim() });
      // The new identity is signed in (argus_identity set): sync the store, then enrol TOTP
      // (required) — enrolment continues to the firm picker.
      await refresh();
      await navigate({ to: res.totp_enrolment_required ? '/login/totp-enrol' : '/firms' });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleAccept(): Promise<void> {
    setError(null);
    setRefusal(null);
    setBusy(true);
    try {
      await api.invites.accept(token);
      await navigate({ to: '/firms' });
    } catch (err) {
      const reason = inviteSignInReason(err);
      if (reason) setRefusal(reason);
      else setError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <AuthCard title="Accept your invitation">
        <p role="status" style={{ margin: 0, fontSize: 14 }}>
          Loading invitation…
        </p>
      </AuthCard>
    );
  }

  if (loadError || !meta) {
    return (
      <AuthCard title="Invitation unavailable">
        <div style={{ display: 'grid', gap: 12 }}>
          <FormError
            message={
              loadError ??
              'This invitation is invalid or has expired. Ask the firm to send a new one.'
            }
          />
          <Link to="/login" style={{ fontSize: 13 }}>
            Back to sign in
          </Link>
        </div>
      </AuthCard>
    );
  }

  const title = meta.firm_name ? `Join ${meta.firm_name}` : 'Accept your invitation';
  const roleLabel = formatRole(meta.role);

  // ── A brand-new account ──────────────────────────────────────────────────────────
  if (!meta.account_exists) {
    return (
      <AuthCard title={title} subtitle="Set up your account">
        <form
          onSubmit={handleCreate}
          aria-label="Accept invitation"
          style={{ display: 'grid', gap: 12 }}
        >
          {/* Readonly, and first in the form: the username a password manager pairs the new
              password with — and the account the user can see they are setting it for. */}
          <ReadonlyIdentityField label="Work email" name="email" value={meta.email} />
          <TextField
            label="Full name"
            name="full_name"
            value={fullName}
            onChange={setFullName}
            autoComplete="name"
          />
          <PasswordField
            label="Password"
            name="password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            placeholder={`At least ${MIN_PASSWORD} characters`}
            {...(error ? { describedBy: errorId } : {})}
          />
          <FormError message={error} id={errorId} />
          <SubmitButton disabled={busy || fullName.trim() === '' || password.length < MIN_PASSWORD}>
            {busy ? 'Creating account…' : 'Create account'}
          </SubmitButton>
        </form>
      </AuthCard>
    );
  }

  // ── An existing account ──────────────────────────────────────────────────────────
  const signedIn = status === 'authenticated' && identity !== null;
  const isInvitee = signedIn && sameEmail(identity.email, meta.email);
  const signedInAs = signedIn ? identity.email : null;
  // A client-side mismatch is shown up front (no request); the server's refusal is the authority.
  const shownRefusal: InviteSignInReason | null =
    refusal ?? (signedIn && !isInvitee ? 'identity_mismatch' : null);

  return (
    <AuthCard
      title={title}
      subtitle={`This invitation is for ${meta.email} (${roleLabel}). You already have an ARGUS account.`}
    >
      <div style={{ display: 'grid', gap: 12 }} aria-label="Accept invitation" role="group">
        {shownRefusal ? (
          <>
            <p
              role="alert"
              style={{ margin: 0, fontSize: 14, color: 'var(--auth-danger, #b42318)' }}
            >
              {refusalCopy(shownRefusal, meta.email, signedInAs)}
            </p>
            {shownRefusal === 'identity_mismatch' ? (
              <SubmitButton type="button" onClick={() => void signOutThenSignIn()}>
                {`Sign out and sign in as ${meta.email}`}
              </SubmitButton>
            ) : (
              <SubmitButton type="button" onClick={() => void goSignIn()}>
                Sign in again
              </SubmitButton>
            )}
          </>
        ) : isInvitee ? (
          <>
            <p style={{ margin: 0, fontSize: 14 }}>Signed in as {identity.email}.</p>
            <FormError message={error} />
            <SubmitButton type="button" disabled={busy} onClick={() => void handleAccept()}>
              {busy ? 'Accepting…' : 'Accept invitation'}
            </SubmitButton>
          </>
        ) : (
          <>
            <p style={{ margin: 0, fontSize: 14 }}>Sign in as {meta.email} to accept.</p>
            <SubmitButton type="button" onClick={() => void goSignIn()}>
              Sign in to accept
            </SubmitButton>
          </>
        )}
        {signedIn ? (
          <button type="button" style={linkButton} onClick={() => void signOutThenSignIn()}>
            Not you? Sign out
          </button>
        ) : null}
      </div>
    </AuthCard>
  );
}
